import { Menu, MenuItem, Skeleton, Stack } from '@mui/material';
import { DotsThreeVerticalIcon } from '@phosphor-icons/react';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Text, api, getQueryProps, isDefined } from 'common';

import { AgenticChatDeleteDialog } from '../../components/AgenticChat/Sessions/DeleteDialog';
import { AgenticChatRenameDialog } from '../../components/AgenticChat/Sessions/RenameDialog';
import { AgenticChatToolbarButton } from '../../components/AgenticChat/ToolbarButton';
import { useAgenticChatContext } from '../../components/AgenticChat/context';

interface AgenticChatToolbarTitleProps {
  sessionId: string;
}

export function AgenticChatToolbarTitle(props: AgenticChatToolbarTitleProps) {
  const { sessionId } = props;
  const { clearDeletedSession } = useAgenticChatContext();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedAction, setSelectedAction] = useState<'Rename' | 'Delete'>();
  const {
    data: session,
    isPending,
    refetch,
  } = useQuery({
    ...getQueryProps(api.client.AgenticPlatformSession, 'findOne', sessionId),
  });
  const { refetch: refetchSessions } = useQuery({
    ...getQueryProps(api.client.AgenticPlatformSession, 'findAll', {
      filterMode: 'OnlySessionsWithMessages',
    }),
    enabled: false,
  });
  const sessionTitle = session?.title ?? 'Flow AI';

  return (
    <Stack direction="row" alignItems="center" gap={1} sx={{ minWidth: 0 }}>
      {isPending ? (
        <Skeleton variant="text" width={120} sx={{ flexShrink: 1 }} />
      ) : (
        <Text
          variant="body2"
          bold
          color="inherit"
          noWrap
          title={sessionTitle}
          sx={{ flex: 1, minWidth: 0 }}
        >
          {sessionTitle}
        </Text>
      )}
      {isDefined(session) && (
        <AgenticChatToolbarButton
          label={`Chat actions for ${sessionTitle}`}
          icon={<DotsThreeVerticalIcon />}
          aria-haspopup="menu"
          aria-expanded={isDefined(menuAnchor)}
          aria-controls={isDefined(menuAnchor) ? 'active-chat-actions' : undefined}
          onClick={(event) => setMenuAnchor(event.currentTarget)}
        />
      )}
      <Menu
        anchorEl={menuAnchor}
        open={isDefined(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        slotProps={{ list: { id: 'active-chat-actions', 'aria-label': 'Chat actions' } }}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setSelectedAction('Rename');
          }}
        >
          Rename
        </MenuItem>
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            setSelectedAction('Delete');
          }}
        >
          Delete
        </MenuItem>
      </Menu>
      <AgenticChatRenameDialog
        currentTitle={session?.title ?? ''}
        sessionId={sessionId}
        isOpen={selectedAction === 'Rename'}
        onClose={() => setSelectedAction(undefined)}
        onRenamed={() => {
          void refetch();
          void refetchSessions();
        }}
      />
      <AgenticChatDeleteDialog
        sessionId={sessionId}
        title={sessionTitle}
        isOpen={selectedAction === 'Delete'}
        onClose={() => setSelectedAction(undefined)}
        onDeleted={() => {
          void refetchSessions();
          clearDeletedSession(sessionId);
        }}
      />
    </Stack>
  );
}
